// database-meta.ts - monitoringFolder와 monitoringInstance 스토어를 통합

import { ref } from 'vue';
import { defineStore } from 'pinia';
import { type DBType } from '@/common/utils';
import type { InstanceGroupInfo, InstanceInfo } from '@/database/utils/pa/pa.types';

const getInstanceByDbType =
  (dbType: string) =>
  (instance: { type?: DBType }): boolean => {
    return instance.type === dbType;
  };

const useInstanceGroup = () => {
  const selectedGroupId = ref<string>('');
  const detailedGroupId = ref<string>('');
  const instanceGroups = ref<InstanceGroupInfo[]>([]);

  // Folder 관련 메서드
  const initInstanceGroups = () => {
    instanceGroups.value = [];
  };

  const setInstanceGroups = (newInstanceGroups: InstanceGroupInfo[]) => {
    instanceGroups.value = newInstanceGroups;
  };

  const getInstanceGroups = () => {
    return instanceGroups.value;
  };

  const getInstanceGroupById = (groupId: string) => {
    return instanceGroups.value.find((group) => group.folderId === groupId);
  };

  const getInstancesById = ({
    groupId,
    instanceId,
  }: {
    groupId: string;
    instanceId: string;
  }): InstanceInfo | undefined => {
    return getInstanceGroupById(groupId)?.instances?.find(
      (instance) => instance.instanceId === instanceId,
    );
  };

  const getInstanceGroupsByDbType = (dbType: NonNullable<DBType>) => {
    return instanceGroups.value.filter(
      (group) => !!group.instances?.length && group.instances.some(getInstanceByDbType(dbType)),
    );
  };

  return {
    selectedGroupId,
    detailedGroupId,
    instanceGroups,

    initInstanceGroups,
    setInstanceGroups,
    getInstanceGroups,
    getInstanceGroupById,
    getInstancesById,
    getInstanceGroupsByDbType,
  };
};

const useInstance = () => {
  // Instance 관련 상태
  const instances = ref<InstanceInfo[]>([]);
  const instanceInfo = ref<InstanceInfo>({
    name: '',
    instanceId: '',
  });

  const initInstances = () => {
    instances.value = [];
    instanceInfo.value = {
      name: '',
      instanceId: '',
    };
  };

  const setInstances = (newInstances: InstanceInfo[]) => {
    instances.value = newInstances;
  };

  const setInstanceInfo = (newInstanceInfo: InstanceInfo) => {
    instanceInfo.value = newInstanceInfo;
  };

  const getInstancesByDbType = (dbType: NonNullable<DBType>) => {
    return instances.value.filter(getInstanceByDbType(dbType));
  };

  const getInstanceById = (instanceId: string) => {
    return instances.value.find((instance) => instance.instanceId === instanceId);
  };

  const getInstanceVersionById = (instanceId: string) => {
    return getInstanceById(instanceId)?.instanceState?.version ?? null;
  };

  return {
    instances,
    instanceInfo,

    initInstances,
    setInstances,
    setInstanceInfo,
    getInstancesByDbType,
    getInstanceById,
    getInstanceVersionById,
  };
};

export const useDatabaseMetaStore = defineStore('databaseMeta', () => {
  const {
    selectedGroupId,
    detailedGroupId,
    instanceGroups,

    initInstanceGroups,
    setInstanceGroups,
    getInstanceGroups,
    getInstanceGroupById,
    getInstancesById,
    getInstanceGroupsByDbType,
  } = useInstanceGroup();

  const {
    instances,
    instanceInfo,

    initInstances,
    setInstances,
    setInstanceInfo,
    getInstancesByDbType,
    getInstanceById,
    getInstanceVersionById,
  } = useInstance();

  // 전체 초기화 메서드
  const initAll = () => {
    initInstanceGroups();
    initInstances();

    selectedGroupId.value = '';
    detailedGroupId.value = '';
  };

  return {
    selectedGroupId,
    detailedGroupId,
    instanceGroups,

    instances,
    instanceInfo,

    initInstanceGroups,
    setInstanceGroups,
    getInstanceGroups,
    getInstanceGroupById,
    getInstancesById,
    getInstanceGroupsByDbType,

    initInstances,
    setInstances,
    setInstanceInfo,
    getInstancesByDbType,
    getInstanceById,
    getInstanceVersionById,

    initAll,
  };
});
